<template>
  <div class="space-y-3">
    <div class="flex items-center justify-between gap-2 flex-wrap">
      <h4 class="text-sm font-medium text-slate-200">Agendamentos</h4>
      <button
        type="button"
        class="text-xs px-3 py-1.5 rounded border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10"
        @click="addScheduleRow"
      >
        + Agendamento
      </button>
    </div>

    <div
      v-if="!hasRows"
      class="text-sm text-amber-50/95 leading-relaxed font-medium bg-amber-500/15 border border-amber-400/45 rounded-lg px-3 py-3 shadow-[0_0_0_1px_rgba(251,191,36,0.08)]"
    >
      Ainda não há nenhum agendamento. Clique em + Agendamento acima para criar e defina recorrência, canais de saída, instruções ou parâmetros fixos.
    </div>

    <div v-for="(row, idx) in visibleRows" :key="row.clientKey || row.id || idx" class="rounded-lg border border-slate-600/60 bg-slate-900/40 overflow-hidden">
      <div
        class="flex flex-col gap-2.5 px-3 py-2.5 bg-slate-900/60 border-b border-slate-600/40 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2"
      >
        <button
          type="button"
          class="flex w-full min-w-0 items-center gap-2 text-left rounded-md py-1 pl-0.5 pr-1 hover:bg-slate-800/50 transition sm:flex-1 sm:w-auto sm:-my-1 sm:-ml-1 sm:pl-1 sm:pr-2"
          :aria-expanded="isOpen(idx)"
          :aria-controls="`schedule-panel-${accordionId}-${idx}`"
          :id="`schedule-trigger-${accordionId}-${idx}`"
          :title="scheduleAccordionNativeTitle(row, idx)"
          @click="toggleAccordion(idx)"
        >
          <svg
            class="h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200"
            :class="isOpen(idx) ? 'rotate-90' : ''"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
          <span class="flex min-w-0 flex-1 items-baseline gap-1.5 overflow-hidden">
            <span class="text-sm font-medium text-slate-200 shrink-0">Agendamento {{ idx + 1 }}</span>
            <span
              v-if="descriptionTitleSnippet(row)"
              class="text-xs text-slate-500 truncate min-w-0"
              :title="String(row.description || '').trim()"
            >
              ({{ descriptionTitleSnippet(row) }})
            </span>
          </span>
        </button>
        <div
          class="flex w-full shrink-0 items-center justify-between gap-3 border-t border-slate-600/35 pt-2 sm:w-auto sm:justify-end sm:gap-3 sm:border-0 sm:pt-0 sm:ml-auto"
        >
          <label class="flex items-center gap-2 cursor-pointer select-none shrink-0">
            <span class="text-sm text-slate-300">Ativar</span>
            <span class="relative inline-block h-5 w-9 align-middle">
              <input v-model="row.enabled" type="checkbox" class="peer sr-only" />
              <span
                class="absolute inset-0 rounded-full bg-slate-600 transition peer-focus-visible:ring-2 peer-focus-visible:ring-cyan-400/50 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-slate-900 peer-checked:bg-cyan-500/75"
                aria-hidden="true"
              />
              <span
                class="pointer-events-none absolute left-0.5 top-1/2 h-3 w-3 -translate-y-1/2 translate-x-0 rounded-full bg-white shadow transition-transform duration-200 ease-out peer-checked:translate-x-5"
                aria-hidden="true"
              />
            </span>
          </label>
          <button
            type="button"
            class="text-xs text-red-300/90 hover:text-red-200 px-2 py-1 -mr-1 rounded-md hover:bg-red-500/10"
            @click.stop="confirmRemoveSchedule(row)"
          >
            Remover
          </button>
        </div>
      </div>

      <div
        v-show="isOpen(idx)"
        :id="`schedule-panel-${accordionId}-${idx}`"
        role="region"
        :aria-labelledby="`schedule-trigger-${accordionId}-${idx}`"
        class="p-3 space-y-3"
      >
        <template v-if="specialty === 'data_analyst' && showScheduleSourceSelect">
          <div v-if="dataAnalystSourceKind === 'api_endpoint'">
            <label
              class="block text-xs font-medium text-slate-300 mb-1"
              :for="`sch-api-${rowKey(row, idx)}`"
            >Endpoint de API</label>
            <select
              :id="`sch-api-${rowKey(row, idx)}`"
              class="schedule-select-dark w-full px-3 py-2 rounded-lg border border-slate-500/70 bg-slate-900/50 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
              :value="scheduleApiSelectValue(row)"
              @change="onScheduleApiIdChange(row, $event)"
            >
              <option value="" disabled>Selecione o endpoint…</option>
              <option
                v-for="opt in scheduleApiEndpointChoices"
                :key="`sch-api-opt-${opt.id}`"
                :value="String(opt.id)"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
          <div v-else>
            <label
              class="block text-xs font-medium text-slate-300 mb-1"
              :for="`sch-db-${rowKey(row, idx)}`"
            >View de base de dados</label>
            <select
              :id="`sch-db-${rowKey(row, idx)}`"
              class="schedule-select-dark w-full px-3 py-2 rounded-lg border border-slate-500/70 bg-slate-900/50 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
              :value="scheduleDbSelectValue(row)"
              @change="onScheduleDbIdChange(row, $event)"
            >
              <option value="" disabled>Selecione a view…</option>
              <option
                v-for="opt in scheduleDatabaseViewChoices"
                :key="`sch-db-opt-${opt.id}`"
                :value="String(opt.id)"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
        </template>

        <div>
          <label class="block text-xs font-medium text-slate-300 mb-1">Descrição</label>
          <input
            v-model="row.description"
            type="text"
            placeholder="Ex.: Relatório diário para financeiro"
            class="w-full px-3 py-2 rounded-lg border border-slate-500/70 bg-slate-900/50 text-slate-100 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
          />
        </div>

        <AgentSchedules
          :settings="row.settings"
          :schedule-enabled="row.enabled"
          :week-day-options="weekDayOptions"
          :whatsapp-delivery-connector-options="whatsappDeliveryConnectorOptions"
          :hide-output-channels="hideScheduleOutputChannels"
        />

        <template v-if="specialty === 'data_analyst'">
          <div class="rounded-lg border border-cyan-400/25 bg-slate-950/35 p-3 space-y-2">
            <h4 class="text-sm font-medium text-slate-200">Instruções para esta execução agendada</h4>
            <p class="text-xs text-slate-400 leading-snug">
              Opcional. Se preenchido, substitui o prompt de sistema do agente só nas runs deste agendamento.
            </p>
            <textarea
              v-model="row.settings.data_analyst.scheduled_system_prompt"
              rows="5"
              class="w-full px-3 py-2 rounded-lg border border-slate-500/70 bg-slate-900/50 text-slate-100 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 font-mono"
              placeholder="Ex.: Gere um resumo executivo…"
            />
          </div>
          <div class="rounded-lg border border-cyan-400/25 bg-slate-950/35 p-3 space-y-2">
            <h4 class="text-sm font-medium text-slate-200">Parâmetros fixos pra execução do agendamento</h4>
            <p class="text-xs text-slate-400 leading-snug">JSON conforme o schema da API ou da view.</p>
            <textarea
              :value="scheduledFixedArgsTextByRow[row.clientKey || row.id || idx]"
              rows="5"
              class="w-full px-3 py-2 rounded-lg border border-slate-500/70 bg-slate-900/50 text-slate-100 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 font-mono"
              placeholder="{}"
              @input="onScheduledFixedArgsInput(row, $event)"
            />
          </div>
        </template>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="deleteConfirmScheduleRow"
        class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-[90] p-4"
        @click.self="closeScheduleRemoveModal"
      >
        <div
          class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-2xl p-6 max-h-[90vh] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70"
        >
          <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
            <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
                clip-rule="evenodd"
              />
            </svg>
            Confirmação!
          </h3>
          <p class="text-slate-300 mb-6">
            Confirma a remoção do agendamento
            <strong class="text-slate-100">{{ scheduleRemoveConfirmLabel }}</strong>?<br />
          </p>
          <div class="flex justify-end space-x-3">
            <button
              type="button"
              class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition"
              @click="executeScheduleRemove"
            >
              Sim
            </button>
            <button
              type="button"
              class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition"
              @click="closeScheduleRemoveModal"
            >
              Não
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import AgentSchedules from './AgentSchedules.vue'

export default {
  name: 'AgentExecutorSchedulesSection',
  components: { AgentSchedules },
  props: {
    form: { type: Object, required: true },
    specialty: { type: String, required: true },
    weekDayOptions: { type: Array, default: () => [] },
    whatsappDeliveryConnectorOptions: { type: Array, default: () => [] },
    /** Analista de e-mail: agendamento sem bloco E-mail/WhatsApp (só recorrência). */
    hideScheduleOutputChannels: { type: Boolean, default: false },
    /** `api_endpoint` | `database_view` — só usado com specialty data_analyst. */
    dataAnalystSourceKind: { type: String, default: 'api_endpoint' },
    /** Endpoints permitidos no agente (labels para checkboxes por agendamento). */
    scheduleApiEndpointChoices: { type: Array, default: () => [] },
    scheduleDatabaseViewChoices: { type: Array, default: () => [] }
  },
  data() {
    return {
      scheduledFixedArgsTextByRow: {},
      /** -1 = todos os agendamentos fechados ao abrir o formulário */
      openAccordionIndex: -1,
      accordionId: `sch-${Math.random().toString(36).slice(2, 9)}`,
      deleteConfirmScheduleRow: null
    }
  },
  computed: {
    scheduleRemoveConfirmLabel() {
      const row = this.deleteConfirmScheduleRow
      if (!row) return ''
      const d = String(row.description || '').trim()
      if (d) return d
      const n = this.visibleRows.indexOf(row)
      return n >= 0 ? `Agendamento ${n + 1}` : 'este agendamento'
    },
    visibleRows() {
      const rows = Array.isArray(this.form.agent_schedules) ? this.form.agent_schedules : []
      return rows.filter((r) => !r._destroy)
    },
    hasRows() {
      return this.visibleRows.length > 0
    },
    showScheduleSourceSelect() {
      if (this.specialty !== 'data_analyst') return false
      if (this.dataAnalystSourceKind === 'api_endpoint') {
        return this.scheduleApiEndpointChoices.length > 0
      }
      return this.scheduleDatabaseViewChoices.length > 0
    }
  },
  watch: {
    visibleRows: {
      deep: true,
      handler(rows) {
        if (this.specialty === 'data_analyst') {
          rows.forEach((r) => this.ensureRowDataAnalyst(r))
        }
        this.syncAllFixedArgsTexts()
        if (rows.length === 0) {
          this.openAccordionIndex = -1
          return
        }
        // Índice fora do intervalo (ex.: 1 com uma única linha): antes fazia rows.length-1 → 0 e abria o 1.º.
        if (this.openAccordionIndex >= rows.length) {
          this.openAccordionIndex = -1
        }
      }
    },
    dataAnalystSourceKind(newVal, oldVal) {
      if (this.specialty !== 'data_analyst' || newVal === oldVal) return
      this.visibleRows.forEach((row) => {
        this.ensureRowDataAnalyst(row)
        const da = row.settings.data_analyst
        da.scheduled_api_connector_endpoint_id = null
        da.scheduled_database_connector_view_id = null
        this.ensureRowDataAnalyst(row)
      })
    }
  },
  mounted() {
    this.ensureAgentSchedulesArray()
    if (this.specialty === 'data_analyst') {
      this.visibleRows.forEach((r) => this.ensureRowDataAnalyst(r))
    }
    this.syncAllFixedArgsTexts()
    this.$nextTick(() => {
      this.openAccordionIndex = -1
    })
  },
  methods: {
    descriptionTitleSnippet(row) {
      const d = String(row.description || '').trim()
      if (!d) return ''
      const max = 48
      return d.length > max ? `${d.slice(0, max)}…` : d
    },
    scheduleAccordionNativeTitle(row, idx) {
      const d = String(row.description || '').trim()
      if (!d) return `Agendamento ${idx + 1}`
      return `Agendamento ${idx + 1} (${d})`
    },
    isOpen(idx) {
      return this.openAccordionIndex === idx
    },
    toggleAccordion(idx) {
      this.openAccordionIndex = this.openAccordionIndex === idx ? -1 : idx
    },
    ensureAgentSchedulesArray() {
      if (!Array.isArray(this.form.agent_schedules)) {
        this.form.agent_schedules = []
      }
    },
    defaultScheduleSettings() {
      return {
        schedule: {
          enabled: false,
          mode: 'interval_minutes',
          every_minutes: 60,
          at_time: '08:00',
          weekdays: [1, 2, 3, 4, 5]
        },
        outputs: {
          email: { enabled: false, to: '' },
          whatsapp: { enabled: false, whatsapp_delivery_connector_id: null, to: '' }
        },
        data_analyst: {
          scheduled_system_prompt: '',
          scheduled_fixed_arguments: {},
          scheduled_api_connector_endpoint_id: null,
          scheduled_database_connector_view_id: null
        }
      }
    },
    addScheduleRow() {
      this.ensureAgentSchedulesArray()
      this.form.agent_schedules.push({
        id: null,
        _destroy: false,
        clientKey: `new-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        enabled: false,
        description: '',
        position: this.form.agent_schedules.length,
        settings: this.defaultScheduleSettings()
      })
      this.syncAllFixedArgsTexts()
      this.$nextTick(() => {
        this.openAccordionIndex = this.visibleRows.length - 1
      })
    },
    confirmRemoveSchedule(row) {
      this.deleteConfirmScheduleRow = row
    },
    closeScheduleRemoveModal() {
      this.deleteConfirmScheduleRow = null
    },
    executeScheduleRemove() {
      const row = this.deleteConfirmScheduleRow
      if (!row) return
      this.closeScheduleRemoveModal()
      this.removeScheduleRow(row)
    },
    removeScheduleRow(row) {
      const idx = this.visibleRows.indexOf(row)
      const prevOpen = this.openAccordionIndex
      if (row.id) {
        row._destroy = true
      } else {
        const i = this.form.agent_schedules.indexOf(row)
        if (i >= 0) this.form.agent_schedules.splice(i, 1)
      }
      this.$nextTick(() => {
        const n = this.visibleRows.length
        if (n === 0) {
          this.openAccordionIndex = -1
          return
        }
        if (prevOpen === idx) {
          this.openAccordionIndex = Math.min(idx, n - 1)
        } else if (prevOpen > idx) {
          this.openAccordionIndex = prevOpen - 1
        }
      })
    },
    rowKey(row, idx) {
      return row.clientKey || row.id || `idx-${idx}`
    },
    ensureRowDataAnalyst(row) {
      if (!row.settings || typeof row.settings !== 'object') {
        row.settings = this.defaultScheduleSettings()
      }
      if (!row.settings.data_analyst || typeof row.settings.data_analyst !== 'object') {
        row.settings.data_analyst = { ...this.defaultScheduleSettings().data_analyst }
      }
      const da = row.settings.data_analyst
      delete da.scheduled_api_connector_endpoint_ids
      delete da.scheduled_database_connector_view_ids
      const apiN = Number(da.scheduled_api_connector_endpoint_id)
      da.scheduled_api_connector_endpoint_id =
        Number.isFinite(apiN) && apiN > 0 ? apiN : null
      const dbN = Number(da.scheduled_database_connector_view_id)
      da.scheduled_database_connector_view_id =
        Number.isFinite(dbN) && dbN > 0 ? dbN : null
      if (this.dataAnalystSourceKind === 'api_endpoint' && this.scheduleApiEndpointChoices.length === 1) {
        da.scheduled_api_connector_endpoint_id = this.scheduleApiEndpointChoices[0].id
      }
      if (this.dataAnalystSourceKind === 'database_view' && this.scheduleDatabaseViewChoices.length === 1) {
        da.scheduled_database_connector_view_id = this.scheduleDatabaseViewChoices[0].id
      }
    },
    scheduleApiSelectValue(row) {
      this.ensureRowDataAnalyst(row)
      const v = row.settings.data_analyst.scheduled_api_connector_endpoint_id
      if (v == null || v === '') return ''
      return String(v)
    },
    scheduleDbSelectValue(row) {
      this.ensureRowDataAnalyst(row)
      const v = row.settings.data_analyst.scheduled_database_connector_view_id
      if (v == null || v === '') return ''
      return String(v)
    },
    onScheduleApiIdChange(row, ev) {
      this.ensureRowDataAnalyst(row)
      const raw = ev.target.value
      const n = raw === '' ? null : Number(raw)
      row.settings.data_analyst.scheduled_api_connector_endpoint_id =
        Number.isFinite(n) && n > 0 ? n : null
    },
    onScheduleDbIdChange(row, ev) {
      this.ensureRowDataAnalyst(row)
      const raw = ev.target.value
      const n = raw === '' ? null : Number(raw)
      row.settings.data_analyst.scheduled_database_connector_view_id =
        Number.isFinite(n) && n > 0 ? n : null
    },
    syncAllFixedArgsTexts() {
      if (this.specialty !== 'data_analyst') return
      this.visibleRows.forEach((row, idx) => {
        this.ensureRowDataAnalyst(row)
        const k = this.rowKey(row, idx)
        const o = row.settings?.data_analyst?.scheduled_fixed_arguments
        try {
          this.scheduledFixedArgsTextByRow = {
            ...this.scheduledFixedArgsTextByRow,
            [k]: o && typeof o === 'object' && !Array.isArray(o) ? JSON.stringify(o, null, 2) : '{}'
          }
        } catch {
          this.scheduledFixedArgsTextByRow = { ...this.scheduledFixedArgsTextByRow, [k]: '{}' }
        }
      })
    },
    onScheduledFixedArgsInput(row, e) {
      const idx = this.visibleRows.indexOf(row)
      const k = this.rowKey(row, idx)
      const t = e.target.value
      this.scheduledFixedArgsTextByRow = { ...this.scheduledFixedArgsTextByRow, [k]: t }
      const da = row.settings.data_analyst
      if (!da) return
      const trimmed = t.trim()
      if (!trimmed) {
        da.scheduled_fixed_arguments = {}
        return
      }
      try {
        const p = JSON.parse(trimmed)
        if (p !== null && typeof p === 'object' && !Array.isArray(p)) {
          da.scheduled_fixed_arguments = p
        }
      } catch {
        /* inválido: mantém último objeto válido */
      }
    },
    validateScheduledFixedJsonForRows() {
      if (this.specialty !== 'data_analyst') return null
      const srcErr = this.validateScheduledDataSourcesForRows()
      if (srcErr) return srcErr
      for (let i = 0; i < this.visibleRows.length; i++) {
        const row = this.visibleRows[i]
        const k = this.rowKey(row, i)
        const t = (this.scheduledFixedArgsTextByRow[k] || '').trim()
        if (!t) continue
        try {
          const p = JSON.parse(t)
          if (p !== null && typeof p === 'object' && !Array.isArray(p)) continue
          return `Agendamento ${i + 1}: parâmetros fixos devem ser um objeto JSON.`
        } catch {
          return `Agendamento ${i + 1}: JSON dos parâmetros fixos inválido.`
        }
      }
      return null
    },
    validateScheduledDataSourcesForRows() {
      for (let i = 0; i < this.visibleRows.length; i++) {
        const row = this.visibleRows[i]
        if (!row.enabled) continue
        this.ensureRowDataAnalyst(row)
        const da = row.settings.data_analyst
        if (this.dataAnalystSourceKind === 'api_endpoint') {
          if (this.scheduleApiEndpointChoices.length === 0) continue
          const id = da.scheduled_api_connector_endpoint_id
          if (id == null || id === '' || !Number.isFinite(Number(id)) || Number(id) <= 0) {
            return `Agendamento ${i + 1}: selecione o endpoint de API.`
          }
        } else {
          if (this.scheduleDatabaseViewChoices.length === 0) continue
          const id = da.scheduled_database_connector_view_id
          if (id == null || id === '' || !Number.isFinite(Number(id)) || Number(id) <= 0) {
            return `Agendamento ${i + 1}: selecione a view de base de dados.`
          }
        }
      }
      return null
    }
  }
}
</script>

<style>
/* Nativo <select>/<option>: SO às vezes pinta a 1ª linha (placeholder) com fundo claro; forçar tema escuro. */
select.schedule-select-dark {
  color-scheme: dark;
}
select.schedule-select-dark option {
  background-color: #0f172a;
  color: #f1f5f9;
}
select.schedule-select-dark option:disabled {
  color: #94a3b8;
}
</style>
